import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { DropdownItem } from '@voyagu/radar-design-system';
import { Observable, take } from 'rxjs';
import { BasefieldV2Component } from '../basefield-v2/basefield-v2.component';

@Component({
  selector: 'count-v2',
  templateUrl: './count-v2.component.html',
  styleUrls: ['./count-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountV2Component extends BasefieldV2Component implements OnInit {
  isTitleMenuOpen = false;

  /**
   * BASI-214: the title can double as a picker that writes to a SIBLING control.
   *
   * The search form uses it for the passenger type code - "Adults" is the adult
   * passenger type, so that label is where choosing a different one belongs. The
   * count stays on this field's own key; the picked code goes to `extra.titleKey`
   * (`ptc` on the search form).
   *
   * Kept generic on purpose: count-v2 also renders the flight-request create-task
   * forms (round / oneway / multi), which pass no options and must go on showing a
   * plain title. Everything here is inert until options arrive.
   */
  titleOptions: DropdownItem[] = [];

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    // The options arrive as a stream, and this component is OnPush: the list is
    // fetched over HTTP, so it lands after the first render, and nothing about
    // this field's inputs changes when it does. Subscribing here is what gets the
    // picker on screen - reading a mutated `extra` would leave the row showing a
    // plain title for good.
    const options$ = this.props.extra?.titleOptions$ as Observable<DropdownItem[]> | undefined;

    options$?.pipe(take(1)).subscribe((options) => {
      this.titleOptions = options;
      this.dropStoredCodeNoLongerOffered(options);
      this.changeDetectorRef.markForCheck();
    });
  }

  /**
   * Forget a picked code the list no longer offers.
   *
   * A code leaves the picker by having `enabled` cleared on its row - the
   * no-deploy way to stop offering one, and how ADT left the menu once it was
   * priced automatically. Modify Search still prefills the code its search was
   * made under, so the control can hold a code that is no longer on the list. The
   * title handles that by itself (nothing matches, so nothing is appended), but
   * the value would still be submitted, and the server answers an unoffered code
   * with 400 - a search that used to work would stop working on a form the agent
   * never touched.
   *
   * An empty list is NOT "every code was retired": it is a failed request, or a
   * deployment older than the endpoint. Clearing on that would throw away a valid
   * choice for a reason that has nothing to do with it.
   *
   * Not marked dirty: this is a correction, not something the agent typed.
   */
  private dropStoredCodeNoLongerOffered(options: DropdownItem[]) {
    if (!options.length) {
      return;
    }

    const control = this.titleControl;
    const stored = control?.value;

    if (control == null || stored == null) {
      return;
    }

    if (!options.some((option) => option.value === stored)) {
      control.setValue(null);
    }
  }

  valueChange(value: number) {
    this.formControl.setValue(value);
    this.formControl.markAsDirty();
  }

  get hasTitleOptions(): boolean {
    return this.titleOptions.length > 0;
  }

  /**
   * The picked option is ADDED to the title, not swapped in for it.
   *
   * What the picker adds is a second price for the same trip, not a different
   * search: measured on the live 5Q81, an ADT group beside a JCB base came back on
   * 126 of 126 itineraries - both prices, every route. A title that replaced
   * "Adults" with "Contract bulk (JCB)" promised a JCB-only search we never run.
   *
   * The code goes in, not the menu label: the row is narrow (a count and a stepper
   * share it) and the code is what the offer carries in the results.
   */
  get title(): string {
    const base = this.props.extra?.title;
    const picked = this.selectedTitleOption;

    return picked ? `${base} + ${picked.value}` : base;
  }

  toggleTitleMenu() {
    this.isTitleMenuOpen = !this.isTitleMenuOpen;
  }

  closeTitleMenu() {
    this.isTitleMenuOpen = false;
  }

  selectTitleOption(option: DropdownItem) {
    const control = this.titleControl;

    if (control) {
      // A null value is the way back to the plain title, so the label never
      // becomes a one-way door: the option carrying it clears the choice.
      control.setValue(option.value ?? null);
      control.markAsDirty();
    }

    this.isTitleMenuOpen = false;
  }

  private get selectedTitleOption(): DropdownItem | undefined {
    const value = this.titleControl?.value;

    if (value == null) {
      return undefined;
    }

    return this.titleOptions.find((option) => option.value === value);
  }

  private get titleControl(): AbstractControl | null {
    const key = this.props.extra?.titleKey as string | undefined;

    return key ? this.form.get(key) : null;
  }
}
